// ═══════════════════════════════════════════════════════════
// dinamicaDeBanda — cada banda se mide contra sí misma
// ═══════════════════════════════════════════════════════════
//
// El motor de las dos ondas de la casa, `Transport` y `MiniWave`. No lee audio
// ni pinta nada: recibe amplitudes y devuelve alturas.
//
// ── 🔴 El problema que resuelve ──
//
// Pintar la amplitud tal cual da una onda **casi quieta y con forma fija**. En
// música los graves pesan siempre más que los agudos, así que cada banda vive
// encerrada en su propia franja y la silueta es siempre la misma rampa
// descendente. Medido en `Transport` con la emisión sonando (28.08.26): los
// graves se movían entre 0,82 y 1, los agudos entre 0,32 y 0,78, y la
// desviación típica de cada banda era de 0,05 sobre un recorrido de 0 a 1.
//
// Lo que se pintaba era el reparto medio de energía del espectro, que apenas
// cambia de un segundo a otro. El movimiento, que es lo que hay que ver,
// quedaba encerrado en unos pocos píxeles.
//
// ── Qué hace en su lugar ──
//
// Cada banda lleva su propio suelo y su propio techo, que la siguen, y se pinta
// **cuánto se sale de lo normal EN SU BANDA**. Un agudo flojo que se dobla sube
// tanto como un grave que se dobla, que es lo que hace que la onda entera se
// mueva en vez de ir toda en fila.
//
// No es un vúmetro calibrado y no pretende serlo: sigue leyendo el audio real,
// pero lo que enseña es la dinámica, no la energía absoluta. Para el ojo, que
// es quien mira esto, la dinámica ES el sonido.

// El techo sube al instante y baja despacio: un golpe tiene que verse entero, y
// con un techo que cayera igual de rápido se lo comería él mismo. El suelo, al
// revés: baja de golpe y sube despacio.
const CAIDA_TECHO = 0.02;
const SUBIDA_SUELO = 0.01;

// Rango mínimo entre suelo y techo. Sin él, en un pasaje quieto la ventana se
// cierra tanto que el ruido de fondo se amplifica hasta parecer música.
//
// 🔴 **Estaba en 0,06 y era demasiado poco.** Pablo, 24.09.26: «las barras hacen
// movimientos muy raros». Y los hacían: dentro de una ventana de 0,06 cualquier
// temblor llena la barra entera, así que lo que se veía no era la música sino el
// ruido amplificado ocho veces.
//
// Medido con **la misma música para todos los valores** —936 fotogramas de la
// emisión real capturados del propio componente, 56 bandas—, que es la única
// forma de comparar: entre dos mediciones en vivo suena otra canción y los
// números no valen nada.
//
//     rango   salto medio   clavadas al tope   recorrido de cada barra
//     0,06       0,192           20,3 %               0,75      ← como estaba
//     0,18       0,161            9,7 %               0,75
//     0,25       0,113            3,2 %               0,73
//     0,32       0,077            0,8 %               0,68      ← aquí
//     0,40       0,049            0,1 %               0,56
//     0,50       0,028            0,0 %               0,40
//
// **«Clavadas al tope» es el síntoma que se veía**: con 0,06, una de cada cinco
// barras estaba contra el techo en cualquier instante. Con 0,32 eso desaparece y
// el salto medio baja a la tercera parte.
//
// ⚠️ **0,32 y no más** porque es el último valor antes de que la onda empiece a
// apagarse: a 0,40 el recorrido cae a 0,56 y a 0,50 los golpes de verdad ya no
// llegan arriba —el salto máximo baja de 0,75 a 0,55—. Lo que se quita es el
// ruido; la dinámica se queda.
//
// ⚠️ **No se añade suavizado al caer**, que era lo otro que se probó: con una
// caída de 0,05 por fotograma los números salían iguales. Maquinaria de más para
// nada medible.
const RANGO_MIN = 0.32;

// Suelo de pintado: una barra en cero desaparece y deja un hueco que se lee
// como que falta algo, no como silencio.
const MINIMO = 0.18;

/**
 * Crea el estado de N bandas. Cada onda monta el suyo: son ventanas que se
 * adaptan con el tiempo, así que compartirlas entre dos ondas de distinto
 * tamaño mezclaría historias que no tienen nada que ver.
 *
 * @param {number} n cuántas bandas
 * @param {object} [opciones] `minimo` para el suelo de pintado
 */
export function crearDinamica(n, { minimo = MINIMO } = {}) {
  // El techo no arranca en cero: con 0 la primera muestra saturaría a tope y la
  // onda daría un golpe al aparecer.
  const techo = new Array(n).fill(0.15);
  const suelo = new Array(n).fill(0);

  return {
    /**
     * @param {number[]} valores amplitudes 0..1, una por banda
     * @returns {number[]} alturas 0..1 listas para pintar
     */
    normaliza(valores) {
      const salida = new Array(n);

      for (let i = 0; i < n; i += 1) {
        const v = Number(valores[i]) || 0;

        techo[i] = v > techo[i] ? v : techo[i] + (v - techo[i]) * CAIDA_TECHO;
        suelo[i] = v < suelo[i] ? v : suelo[i] + (v - suelo[i]) * SUBIDA_SUELO;

        const rango = Math.max(RANGO_MIN, techo[i] - suelo[i]);
        const rel = (v - suelo[i]) / rango;

        salida[i] = Math.max(minimo, Math.min(1, rel));
      }

      return salida;
    },
  };
}
